/* ==========================================================================
   WeScaled — theme.js
   Core runtime: atmosphere, scroll reveal, header, drawers, quantity inputs,
   and the small shared helpers every other bundle builds on.

   No framework, no jQuery. Everything is a custom element or a passive
   listener, and every animated effect bails out under prefers-reduced-motion.
   ========================================================================== */

(function () {
  'use strict';

  const cfg = window.theme || (window.theme = {});
  const motion = cfg.motion || {};
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;

  /* --- shared helpers ---------------------------------------------------- */

  const WS = (window.WS = {
    reduced,
    finePointer,

    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    formatMoney(cents) {
      const format = cfg.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const [whole, decimal] = value.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, `${grouped}.${decimal}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
    },

    /* Publish/subscribe so cart, header count, and free-shipping bar can all
       react to one update without knowing about each other. */
    events: new EventTarget(),

    publish(name, detail) {
      WS.events.dispatchEvent(new CustomEvent(name, { detail }));
    },

    subscribe(name, handler) {
      WS.events.addEventListener(name, handler);
      return () => WS.events.removeEventListener(name, handler);
    },

    /* Standard storefront events (Shopify Spring '26). The platform injects
       window.StandardEvents; every call site stays a no-op without it. */
    standard(name) {
      return (window.StandardEvents || {})[name];
    },

    /* Base class for elements that auto-dispatch a `*:view` standard event
       from a `view-event-payload` attribute. Degrades to a plain element. */
    viewEventBase() {
      const factory = window.StandardEvents && window.StandardEvents.createViewEventElement;
      return typeof factory === 'function' ? factory(HTMLElement) : HTMLElement;
    },

    trapFocus(container, focusFirst) {
      const selector =
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

      function onKeydown(event) {
        if (event.key !== 'Tab') return;
        const nodes = Array.from(container.querySelectorAll(selector)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement
        );
        if (!nodes.length) return;

        const first = nodes[0];
        const last = nodes[nodes.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      container.addEventListener('keydown', onKeydown);
      (focusFirst || container.querySelector(selector) || container).focus({ preventScroll: true });

      return () => container.removeEventListener('keydown', onKeydown);
    },

    /* One shared scroll lock so nested overlays don't fight over <body>. */
    _locks: 0,
    lockScroll() {
      if (WS._locks === 0) {
        WS._scrollY = window.scrollY;
        const bar = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (bar > 0) document.body.style.paddingRight = `${bar}px`;
      }
      WS._locks += 1;
    },
    unlockScroll() {
      WS._locks = Math.max(0, WS._locks - 1);
      if (WS._locks === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    },
  });

  /* --- standard view events ------------------------------------------------

     A generic wrapper that fires shopify:product:view / :collection:view when
     it scrolls into view, reading its payload from the `view-event-payload`
     attribute produced by the `standard_event_data` Liquid filter.
     ------------------------------------------------------------------------ */

  class ViewEvent extends WS.viewEventBase() {}
  customElements.define('view-event', ViewEvent);

  /* --- atmosphere: cursor glow ------------------------------------------- */

  const glow = document.querySelector('[data-glow]');
  if (glow && motion.glow !== false && !reduced && finePointer) {
    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let cx = tx;
    let cy = ty;
    let shown = false;
    let raf = null;

    function loop() {
      cx += (tx - cx) * 0.065;
      cy += (ty - cy) * 0.065;
      glow.style.transform = `translate3d(${cx - 320}px, ${cy - 320}px, 0)`;
      raf = Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1 ? requestAnimationFrame(loop) : null;
    }

    window.addEventListener(
      'pointermove',
      (e) => {
        tx = e.clientX;
        ty = e.clientY;
        if (!shown) {
          glow.style.opacity = '1';
          shown = true;
        }
        if (!raf) raf = requestAnimationFrame(loop);
      },
      { passive: true }
    );
  }

  /* --- atmosphere: drifting dust ----------------------------------------- */

  const dust = document.querySelector('[data-dust]');
  if (dust && motion.dust !== false && !reduced) {
    const ctx = dust.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let W = 0;
    let H = 0;
    let running = true;
    let last = performance.now();

    function size() {
      W = window.innerWidth;
      H = window.innerHeight;
      dust.width = W * dpr;
      dust.height = H * dpr;
      dust.style.width = `${W}px`;
      dust.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function seed() {
      const count = Math.min(70, Math.floor((W * H) / 22000));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: 0.6 + Math.random() * 1.5,
        vx: (Math.random() - 0.5) * 0.12,
        vy: -0.05 - Math.random() * 0.14,
        a: 0.08 + Math.random() * 0.22,
        ph: Math.random() * Math.PI * 2,
        tw: 0.4 + Math.random() * 0.9,
      }));
    }

    function frame(now) {
      if (!running) return;
      const dt = Math.min((now - last) / 16.7, 3);
      last = now;
      ctx.clearRect(0, 0, W, H);

      for (const p of particles) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.ph += 0.012 * p.tw * dt;

        if (p.y < -6) {
          p.y = H + 6;
          p.x = Math.random() * W;
        }
        if (p.x < -6) p.x = W + 6;
        else if (p.x > W + 6) p.x = -6;

        ctx.globalAlpha = p.a * (0.55 + 0.45 * Math.sin(p.ph));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 6.2832);
        ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--ink') || '#fff';
        ctx.fill();
      }

      requestAnimationFrame(frame);
    }

    size();
    seed();
    requestAnimationFrame(frame);

    window.addEventListener(
      'resize',
      WS.debounce(() => {
        size();
        seed();
      }, 200),
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      running = !document.hidden;
      if (running) {
        last = performance.now();
        requestAnimationFrame(frame);
      }
    });
  }

  /* --- scroll reveal ------------------------------------------------------ */

  if (motion.reveal !== false && !reduced && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    const watch = (root) => root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
    watch(document);

    // Theme editor re-renders sections; pick up anything new.
    document.addEventListener('shopify:section:load', (e) => watch(e.target));
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* --- magnetic buttons ---------------------------------------------------- */

  if (motion.magnetic !== false && !reduced && finePointer) {
    const magnetise = (wrapper) => {
      if (wrapper.dataset.magnetised) return;
      wrapper.dataset.magnetised = 'true';

      let tx = 0;
      let ty = 0;
      let cx = 0;
      let cy = 0;
      let raf = null;
      const radius = 150;
      const strength = 10;

      function loop() {
        cx += (tx - cx) * 0.12;
        cy += (ty - cy) * 0.12;
        wrapper.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
        raf = Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05 ? requestAnimationFrame(loop) : null;
      }

      window.addEventListener(
        'pointermove',
        (e) => {
          // Detached by a section reload — stop doing layout work for it.
          if (!wrapper.isConnected) return;

          const rect = wrapper.getBoundingClientRect();
          const dx = e.clientX - (rect.left + rect.width / 2);
          const dy = e.clientY - (rect.top + rect.height / 2);
          const dist = Math.hypot(dx, dy);

          if (dist < radius && dist > 0) {
            const pull = (1 - dist / radius) * strength;
            tx = (dx / dist) * pull;
            ty = (dy / dist) * pull;
          } else {
            tx = 0;
            ty = 0;
          }

          if (!raf) raf = requestAnimationFrame(loop);
        },
        { passive: true }
      );
    };

    const wireMagnets = (root) => root.querySelectorAll('.mag').forEach(magnetise);
    wireMagnets(document);
    document.addEventListener('shopify:section:load', (e) => wireMagnets(e.target));
  }

  /* --- header offset -------------------------------------------------------

     Publishes the real rendered height of the header group as --header-offset
     so full-viewport sections can subtract it exactly. The announcement bar is
     optional and the header wraps at some widths, so this can't be a constant.
     ------------------------------------------------------------------------ */

  function measureHeader() {
    let height = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((part) => {
      height += part.offsetHeight;
    });
    document.documentElement.style.setProperty('--header-offset', `${height}px`);
  }

  measureHeader();
  window.addEventListener('resize', WS.debounce(measureHeader, 150), { passive: true });
  document.addEventListener('shopify:section:load', measureHeader);
  // Web fonts change the header's line box, so remeasure once they land.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureHeader);

  /* --- sticky header shadow ------------------------------------------------ */

  const stickyHeader = document.querySelector('.header-wrapper--sticky');
  if (stickyHeader) {
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    stickyHeader.parentNode.insertBefore(sentinel, stickyHeader);

    new IntersectionObserver(([entry]) => stickyHeader.classList.toggle('is-stuck', !entry.isIntersecting), {
      threshold: 1,
    }).observe(sentinel);
  }

  /* --- generic drawer ------------------------------------------------------ */

  class WsDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.opener = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target.matches('.drawer__overlay')) {
          this.close();
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });

    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      WS.lockScroll();

      const panel = this.querySelector('.drawer__panel') || this;
      // Wait for the transform transition to start so focus doesn't scroll the
      // panel while it's still off-screen.
      requestAnimationFrame(() => {
        this.releaseFocus = WS.trapFocus(panel, panel.querySelector('[data-drawer-close]'));
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      WS.unlockScroll();

      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      if (this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }

  customElements.define('ws-drawer', WsDrawer);

  // Triggers are delegated rather than bound per element, so a section reload
  // in the theme editor can't leave a dead button behind.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;

    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    drawer.open(trigger);
  });

  /* --- quantity input ------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.changeEvent = new Event('change', { bubbles: true });

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.name === 'plus' ? 1 : -1;
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, Number(this.input.value) + step));

          if (next === Number(this.input.value)) return;
          this.input.value = String(next);
          this.input.dispatchEvent(this.changeEvent);
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* --- details/summary dropdowns close on outside click -------------------- */

  document.addEventListener('click', (event) => {
    document.querySelectorAll('details.facet[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details.facet[open]').forEach((details) => {
      details.open = false;
      details.querySelector('summary')?.focus();
    });
  });

  /* --- marquee: duplicate track for a seamless loop ------------------------ */

  function primeMarquees(root) {
    root.querySelectorAll('[data-marquee-track]:not([data-primed])').forEach((track) => {
      track.setAttribute('data-primed', 'true');
      track.innerHTML += track.innerHTML;
    });
  }

  primeMarquees(document);
  document.addEventListener('shopify:section:load', (e) => primeMarquees(e.target));
})();
